import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useTransform, animate, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { Cpu, Smartphone, Globe, Terminal, Cloud } from 'lucide-react';

const fadeInLeft = {
  hidden: { opacity: 0, x: -100 },
  show: { opacity: 1, x: 0, transition: { duration: 1.6, ease: 'easeOut' } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 1, ease: 'easeOut' } },
};

const OurCompany = () => {
  const navigate = useNavigate();
  const count = useMotionValue(0);
  const rounded = useTransform(count, (latest) => Math.round(latest));
  const [displayCount, setDisplayCount] = useState(20);

  // Intersection Observer
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: false,
  });

  const leftControls = useAnimation();
  const rightControls = useAnimation();

  useEffect(() => {
    let controls;
    let unsubscribe;

    if (inView) {
      count.set(0);
      leftControls.start('show');
      rightControls.start('show');

      controls = animate(count, 20, {
        duration: 1.5,
        ease: 'easeOut',
      });

      unsubscribe = rounded.on('change', setDisplayCount);
    } else {
      leftControls.start('hidden');
      rightControls.start('hidden');
    }

    return () => {
      if (controls) controls.stop();
      if (unsubscribe) unsubscribe();
    };
  }, [inView, count, leftControls, rightControls, rounded]);

  const specialties = [
    { label: 'Generative AI', path: '/services/gen-ai', icon: <Cpu className="w-4 h-4" /> }, // Reduced icon size
    { label: 'Mobile App Development', path: '/services/mobile-app-development', icon: <Smartphone className="w-4 h-4" /> },
    { label: 'Web Development', path: '/services/web-development', icon: <Globe className="w-4 h-4" /> },
    { label: 'DevOps Consulting', path: '/services/devops-consulting', icon: <Cloud className="w-4 h-4" /> },
    { label: 'Salesforce Development', path: '/services/Salesforce-services', icon: <Terminal className="w-4 h-4" /> },
  ];

  return (
    <section
      ref={ref}
      className="min-h-screen relative overflow-hidden flex items-center py-16 lg:py-0"
      style={{ backgroundColor: '#1b2f5a' }}
    >
      <div className="container mx-auto px-4 md:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          
          {/* Left Side: Image and Badge */}
          <motion.div
            className="relative group"
            variants={fadeInLeft}
            initial="hidden"
            animate={leftControls}
          >
            <motion.div
              className="absolute -top-6 -left-6 w-16 h-16 opacity-80 z-10 hidden md:block"
              style={{ backgroundColor: '#f7941d' }}
              animate={{ rotate: [0, 90, 180, 270, 360] }}
              transition={{ repeat: Infinity, duration: 10, ease: 'linear' }}
            />

            <motion.div
              className="relative rounded-2xl overflow-hidden shadow-2xl"
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 100 }}
            >
              <motion.img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&q=80&w=1000"
                alt="BytesFarms Technologies Team"
                className="w-full h-[400px] md:h-[550px] object-cover brightness-90 group-hover:brightness-100 transition duration-700"
                initial={{ opacity: 0, scale: 1.1 }}
                animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 1.1 }}
                transition={{ duration: 1.5 }}
              />

              {/* Experience Badge - Reduced font sizes */}
              <motion.div
                className="absolute bottom-6 right-6 px-6 py-4 rounded-xl shadow-2xl backdrop-blur-sm"
                style={{ backgroundColor: 'rgba(247, 148, 29, 0.95)' }}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
                transition={{ delay: 0.8, duration: 0.8 }}
              >
                <div className="text-white text-center">
                  <div className="text-3xl md:text-4xl font-black tracking-tighter">
                    {displayCount}+
                  </div>
                  <div className="text-[10px] md:text-xs font-bold uppercase tracking-widest mt-1 border-t border-white/30 pt-1">
                    Years of Founder's <br /> Experience
                  </div>
                </div>
              </motion.div>
            </motion.div>
            
            <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-white/5 rounded-full blur-3xl -z-10"></div>
          </motion.div>

          {/* Right Side: Content */}
          <motion.div
            className="space-y-6 text-white" // Reduced vertical space
            initial="hidden"
            animate={rightControls}
            variants={{ show: { transition: { staggerChildren: 0.2 } } }}
          >
            <motion.div variants={fadeInUp}>
              <p className="font-bold text-[12px] uppercase tracking-[0.3em] mb-3" style={{ color: '#f7941d' }}>
                ABOUT BYTESFARMS
              </p>
              {/* Reduced from text-4xl/6xl to text-3xl/5xl */}
              <h2 className="text-3xl md:text-5xl lg:text-5xl font-extrabold leading-[1.15]">
                We are a trusted partner <br />
                <span className="text-white/90">for IT solutions</span>
              </h2>
              <div className="w-16 h-1 mt-4 rounded-full" style={{ backgroundColor: '#f7941d' }}></div>
            </motion.div>

            <motion.div variants={fadeInUp} className="space-y-4">
              {/* Reduced from text-lg/xl to text-base/lg */}
              <p className="text-slate-200 text-base md:text-lg leading-relaxed max-w-xl opacity-90">
                BytesFarms Technologies is a trusted IT partner with 20+ years of Founder’s experience. 
                We specialize in:
              </p>

              {/* Specialties List - Reduced font and padding */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {specialties.map((item, index) => (
                  <motion.div 
                    key={index}
                    onClick={() => navigate(item.path)}
                    className="flex items-center space-x-3 bg-white/5 p-2.5 rounded-lg border border-white/10 hover:border-[#f7941d]/50 transition-colors cursor-pointer group/item"
                    whileHover={{ x: 8 }}
                  >
                    <span className="group-hover/item:scale-110 transition-transform duration-300" style={{ color: '#f7941d' }}>
                      {item.icon}
                    </span>
                    <span className="text-sm font-medium text-gray-200 group-hover/item:text-white">
                      {item.label}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default OurCompany;